import { useEffect, useState, type FormEvent } from 'react';
import {
  Truck, Wrench, Package, ArrowRight, Undo2, AlertTriangle, ClipboardCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useRecordMovement, type RecordMovementInput } from '@/hooks/useInventory';
import {
  LOCATION_LABELS, MOVEMENT_TYPE_LABELS,
  type InventoryItem, type Location, type MovementType,
} from '@/types/inventory';

type Props = {
  open: boolean;
  onClose: () => void;
  item: InventoryItem;
  /** Pre-selected type when the modal is opened from a specific action */
  defaultType?: MovementType;
};

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const TYPE_ICONS: Record<MovementType, typeof Truck> = {
  receipt:     Truck,
  consumption: Wrench,
  transfer:    ArrowRight,
  adjustment:  ClipboardCheck,
  return:      Undo2,
  scrap:       AlertTriangle,
  initial:     Package,
};

const TYPE_HINTS: Record<MovementType, string> = {
  receipt:     'New stock arriving from a supplier. Quantity is added.',
  consumption: 'Used on a work order. Quantity is deducted.',
  transfer:    'Moving between locations. Two log entries are created.',
  adjustment:  'Cycle-count correction. Enter the delta (±) from current.',
  return:      'Customer or production return. Quantity is added.',
  scrap:       'Damaged or wasted. Quantity is deducted.',
  initial:     'Seeding stock for a brand-new item.',
};

export function RecordMovementModal({ open, onClose, item, defaultType = 'receipt' }: Props) {
  const record = useRecordMovement();

  const [type, setType] = useState<MovementType>(defaultType);
  const [form, setForm] = useState<Omit<RecordMovementInput, 'itemId' | 'type'>>({
    quantity: 0,
    location: 'receiving',
    occurredAt: nowLocal(),
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setForm({
        quantity: 0,
        location: defaultType === 'receipt' ? 'receiving' : Object.keys(item.onHand ?? {})[0] as Location ?? 'receiving',
        occurredAt: nowLocal(),
        notes: '',
      });
      setError(null);
    }
  }, [open, defaultType, item]);

  function update_<K extends keyof typeof form>(key: K, val: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Adjustment allows negative; others must be > 0
    if (type !== 'adjustment' && form.quantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }
    if (type === 'adjustment' && form.quantity === 0) {
      setError('Enter a non-zero delta (positive or negative).');
      return;
    }
    if (type === 'transfer' && !form.toLocation) {
      setError('Select a destination location.');
      return;
    }
    if (type === 'transfer' && form.location === form.toLocation) {
      setError('Destination must differ from source.');
      return;
    }

    try {
      await record.mutateAsync({
        itemId: item.id,
        type,
        quantity: form.quantity,
        location: form.location,
        toLocation: type === 'transfer' ? form.toLocation : undefined,
        occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
        notes: form.notes,
        orderNumber: form.orderNumber,
        poNumber: form.poNumber,
        batch: form.batch,
        lotNumber: form.lotNumber,
        unitCostAtTime: form.unitCostAtTime,
        reason: form.reason,
      });
      toast.success(`${MOVEMENT_TYPE_LABELS[type]} recorded`);
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to record movement.');
    }
  }

  const Icon = TYPE_ICONS[type];
  const isIn = ['receipt', 'return', 'initial'].includes(type);
  const isOut = ['consumption', 'scrap'].includes(type);

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Stock Movement"
      title={`Record activity on ${item.sku}`}
      description={TYPE_HINTS[type]}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={record.isPending}>Cancel</Button>
          <Button type="submit" form="movement-form" loading={record.isPending} leftIcon={<Icon size={13} />}>
            Record {MOVEMENT_TYPE_LABELS[type]}
          </Button>
        </>
      }
    >
      <form id="movement-form" onSubmit={onSubmit} className="space-y-5 py-2">
        {/* Type selector — visual tab bar */}
        <div>
          <div className="kicker mb-2">Type</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-1 p-1 bg-bg-subtle border border-border rounded-sm">
            {(['receipt', 'consumption', 'transfer', 'adjustment', 'return', 'scrap'] as MovementType[]).map((t) => {
              const TIcon = TYPE_ICONS[t];
              const active = t === type;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`
                    flex flex-col items-center gap-1.5 px-2 py-2.5 rounded-sm transition-colors
                    ${active ? 'bg-bg-panel text-text' : 'text-text-muted hover:text-text hover:bg-bg-hover'}
                  `}
                >
                  <TIcon size={14} className={active ? 'text-brass-300' : ''} />
                  <span className="text-2xs uppercase tracking-[0.1em]">{MOVEMENT_TYPE_LABELS[t]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Core fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={type === 'adjustment' ? 'Delta (± allowed)' : `Quantity (${item.unit})`}
            type="number"
            step="any"
            required
            value={form.quantity || ''}
            onChange={(e) => update_('quantity', Number(e.target.value) || 0)}
            hint={
              isIn  ? 'Quantity will be added to stock.' :
              isOut ? 'Quantity will be deducted from stock.' :
              type === 'adjustment' ? 'Positive adds; negative removes.' :
              undefined
            }
          />
          <Input
            label="When"
            type="datetime-local"
            value={form.occurredAt ?? nowLocal()}
            onChange={(e) => update_('occurredAt', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label={type === 'transfer' ? 'From Location' : 'Location'}
            value={form.location}
            onChange={(e) => update_('location', e.target.value as Location)}
            options={Object.entries(LOCATION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          {type === 'transfer' && (
            <Select
              label="To Location"
              value={form.toLocation ?? ''}
              onChange={(e) => update_('toLocation', e.target.value as Location)}
              options={[
                { value: '', label: '— Select —' },
                ...Object.entries(LOCATION_LABELS).map(([v, l]) => ({ value: v, label: l })),
              ]}
            />
          )}
        </div>

        {/* Source references */}
        {(type === 'receipt' || type === 'return') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="PO Number"
              placeholder="Supplier PO reference"
              value={form.poNumber ?? ''}
              onChange={(e) => update_('poNumber', e.target.value)}
            />
            <Input
              label="Unit Cost at Time"
              type="number" min="0" step="0.01"
              placeholder={item.unitCost?.toString() ?? ''}
              value={form.unitCostAtTime ?? ''}
              onChange={(e) => update_('unitCostAtTime', e.target.value === '' ? undefined : Number(e.target.value))}
              hint="For FIFO valuation. Defaults to item's unit cost."
            />
          </div>
        )}

        {type === 'consumption' && (
          <Input
            label="Work Order Number"
            placeholder="WO-2601"
            value={form.orderNumber ?? ''}
            onChange={(e) => update_('orderNumber', e.target.value)}
            hint="Which work order consumed this?"
          />
        )}

        {item.kind === 'veneer' && (type === 'receipt' || type === 'consumption') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Flitch / Batch"
              placeholder={item.flitchNumber ?? ''}
              value={form.batch ?? ''}
              onChange={(e) => update_('batch', e.target.value)}
            />
            <Input
              label="Lot Number"
              value={form.lotNumber ?? ''}
              onChange={(e) => update_('lotNumber', e.target.value)}
            />
          </div>
        )}

        {(type === 'scrap' || type === 'adjustment') && (
          <Input
            label="Reason"
            placeholder={type === 'scrap' ? 'Damaged in handling, out of spec…' : 'Cycle count variance, re-stocking…'}
            value={form.reason ?? ''}
            onChange={(e) => update_('reason', e.target.value)}
          />
        )}

        <Textarea
          label="Notes"
          rows={3}
          placeholder="Additional context (optional)"
          value={form.notes ?? ''}
          onChange={(e) => update_('notes', e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
